import { collection, doc, setDoc, Timestamp, deleteDoc, getDoc, query, where, getDocs, updateDoc} from "firebase/firestore";
import { firestore } from "../firebase/firebaseConfig";

/**
 * Creates a new transaction in the `transactions` collection.
 * @param {string} transactionId - Unique identifier for the transaction.
 * @param {Object} transactionDetails - Details of the transaction (e.g., userId, amount, listingId).
 * @returns {Promise<void>}
 */
export const createTransaction = async (transactionId, transactionDetails) => {
  try {
    const transactionRef = doc(firestore, "transactions", transactionId);
    const transactionData = {
      ...transactionDetails,
      status: "pending", // Default status
      createdAt: Timestamp.now(),
    };
    await setDoc(transactionRef, transactionData);
    console.log(`[createTransaction] Transaction created successfully with ID: ${transactionId}`);
  } catch (error) {
    console.error("[createTransaction] Error creating transaction:", error);
    throw new Error("Failed to create transaction. Please try again.");
  }
};

/**
 * Fetches a transaction by its ID.
 * @param {string} transactionId - Unique identifier for the transaction.
 * @returns {Promise<Object|null>} The transaction details or null if not found.
 */
export const fetchTransactionById = async (transactionId) => {
  try {
    const transactionRef = doc(firestore, "transactions", transactionId);
    const transactionSnap = await getDoc(transactionRef);

    if (!transactionSnap.exists()) {
      console.warn(`[fetchTransactionById] Transaction not found for ID: ${transactionId}`);
      return null;
    }

    return { id: transactionSnap.id, ...transactionSnap.data() };
  } catch (error) {
    console.error("[fetchTransactionById] Error fetching transaction:", error);
    throw new Error("Failed to fetch transaction.");
  }
};

/**
 * Fetches all transactions for a given user ID.
 * @param {string} userId - The unique ID of the user.
 * @returns {Promise<Array>} An array of transactions.
 */
export const fetchTransactionsByUser = async (userId) => {
  try {
    const transactionsRef = collection(firestore, "transactions");
    const userTransactionsQuery = query(transactionsRef, where("userId", "==", userId));
    const querySnapshot = await getDocs(userTransactionsQuery);

    if (querySnapshot.empty) {
      console.warn(`[fetchTransactionsByUser] No transactions found for user ID: ${userId}`);
      return [];
    }

    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("[fetchTransactionsByUser] Error fetching transactions by user:", error);
    throw new Error("Failed to fetch transactions.");
  }
};

/**
 * Updates the status of a transaction.
 * @param {string} transactionId - Unique ID of the transaction.
 * @param {string} status - The new status (e.g., `completed`, `canceled`, `disputed`).
 * @returns {Promise<void>}
 */
export const updateTransactionStatus = async (transactionId, status) => {
  try {
    const transactionRef = doc(firestore, "transactions", transactionId);
    const updatedData = {
      status,
      ...(status === "completed" && { completedAt: Timestamp.now() }), // Add timestamp if completed
    };
    await updateDoc(transactionRef, updatedData);
    console.log(`[updateTransactionStatus] Transaction ${transactionId} status updated to: ${status}`);
  } catch (error) {
    console.error("[updateTransactionStatus] Error updating transaction status:", error);
    throw new Error("Failed to update transaction status.");
  }
};

/**
 * Fetches all transactions with a specific status.
 * @param {string} status - The transaction status to filter by.
 * @returns {Promise<Array>} An array of transactions.
 */
export const fetchTransactionsByStatus = async (status) => {
  try {
    const transactionsRef = collection(firestore, "transactions");
    const statusQuery = query(transactionsRef, where("status", "==", status));
    const querySnapshot = await getDocs(statusQuery);

    if (querySnapshot.empty) {
      console.warn(`[fetchTransactionsByStatus] No transactions found with status: ${status}`);
      return [];
    }

    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("[fetchTransactionsByStatus] Error fetching transactions by status:", error);
    throw new Error("Failed to fetch transactions by status.");
  }
};

export const fetchTransactionByListingId = async (listingId) => {
    try {
      // Query the `trades` collection where `listingId` matches
      const transactionsRef = collection(firestore, "transactions");
      const transactionQuery = query(transactionsRef, where("listingId", "==", listingId));
  
      const querySnapshot = await getDocs(transactionQuery);
  
      if (querySnapshot.empty) {
        throw new Error("No trade found with the given listing ID.");
      }
  
      // Assuming only one trade exists for the given listingId
      const transationDoc = querySnapshot.docs[0];
      return { id: transationDoc.id, ...transationDoc.data() };
    } catch (error) {
      console.error("Error fetching transactions by listingId:", error);
      throw error;
    }
  };
